import React, { useEffect, useState } from "react";
import {
  Button,
  MenuItem,
  Select,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Box,
  IconButton,
  CircularProgress,
  Typography
} from "@mui/material";
import { Close as CloseIcon, Download as DownloadIcon } from "@mui/icons-material";
import { DataGrid } from "@mui/x-data-grid";
import { toast } from "react-toastify";
import { ColumnConfig } from "../types/ColumnConfig";
import { TablePagination } from "../types/Pagination";
import Search from "../components/SearchInput";
import DateInput from "../components/DateInput";
import { formatToDDMMYYYY } from "../utils/Validation";
import "./Kyc.css";
import "./Common.css";
import "rsuite/dist/rsuite.min.css";
import DropdownSearch from "../components/DropDown";
import { getKycList, getKycDocument, updateKycDocumentStatus, getUserListForFilters } from "../services/apiService";
import CustomTable from "../components/CustomTable";
import ExporterButton from "../components/ExportButton";
interface UpdateKycStatusDialogProps {
  open: boolean;
  currentStatus: string;
  currentMessage: string;
  docId: number;
  onClose: () => void;
  onUpdate: (docId: number, status: string, message: string) => Promise<void>;
  isUpdating: boolean;
  isViewOnly: boolean;
}

const UpdateKycStatusDialog: React.FC<UpdateKycStatusDialogProps> = ({
  open,
  currentStatus,
  currentMessage,
  docId,
  onClose,
  onUpdate,
  isUpdating,
  isViewOnly
}) => {
  const [selectedStatus, setSelectedStatus] = useState(currentStatus);
  const [message, setMessage] = useState(currentMessage || '');

  // Reset form when dialog opens
  useEffect(() => {
    if (open) {
      setSelectedStatus(currentStatus);
      setMessage(currentMessage || '');
    }
  }, [open, currentStatus, currentMessage]);

  // Get allowed status transitions based on current status
  const getAvailableStatusOptions = (currentStatus: string, isViewOnly: boolean) => {
    // If in view-only mode, only show the current status
    if (isViewOnly) {
      return [
        { value: currentStatus.toLowerCase(), label: currentStatus.charAt(0).toUpperCase() + currentStatus.slice(1).toLowerCase() }
      ];
    }
    switch (currentStatus.toLowerCase()) {
      case 'pending':
        return [
          { value: 'pending', label: 'Pending' },
          { value: 'approved', label: 'Approved' },
          { value: 'rejected', label: 'Rejected' }
        ];
      case 'approved':
        return [
          { value: 'approved', label: 'Approved' }
        ];
      case 'rejected':
        return [
          { value: 'rejected', label: 'Rejected' }
        ];
      default:
        return [
          { value: 'pending', label: 'Pending' },
          { value: 'approved', label: 'Approved' },
          { value: 'rejected', label: 'Rejected' }
        ];
    }
  };

  const statusOptions = getAvailableStatusOptions(currentStatus, isViewOnly);

  const handleSubmit = async () => {
    // Don't allow submission in view-only mode
    if (isViewOnly) {
      onClose();
      return;
    }
    if (!selectedStatus) {
      toast.error('Please select a status');
      return;
    }

    // Validate status transition
    const currentStatusLower = currentStatus.toLowerCase();
    const selectedStatusLower = selectedStatus.toLowerCase();

    if (currentStatusLower === 'approved' && selectedStatusLower !== 'approved') {
      toast.error('Approved documents cannot be changed');
      return;
    }

    if (currentStatusLower === 'rejected' && selectedStatusLower !== 'rejected') {
      toast.error('Rejected documents cannot be changed');
      return;
    }

    if (!message.trim()) {
      toast.error('Please provide a message/comment');
      return;
    }

    await onUpdate(docId, selectedStatus, message);
  };

  const handleCancel = () => {
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleCancel} maxWidth="sm" fullWidth>
      <DialogTitle>
        {isViewOnly ? 'View KYC Document Status' : 'Update KYC Document Status'}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          <FormControl fullWidth>
            <InputLabel>Status</InputLabel>
            <Select
              value={selectedStatus}
              label="Status"
              onChange={(e) => setSelectedStatus(e.target.value)}
              disabled={isUpdating || isViewOnly} // Disable in view-only mode
            >
              {statusOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            label="Message / Comment"
            multiline
            rows={4}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder={isViewOnly ? "No comments available" : "Enter your comments about the status update..."}
            disabled={isUpdating || isViewOnly}
            fullWidth
            required={!isViewOnly}
            helperText={isViewOnly ? "Current status comment" : "Please provide a reason or comment for the status change"}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={handleCancel}
          disabled={isUpdating}
          color="inherit"
        >
          {isViewOnly ? 'Close' : 'Cancel'}
        </Button>
        {!isViewOnly && (
          <Button
            onClick={handleSubmit}
            variant="contained"
            color="primary"
            disabled={isUpdating}
          >
            {isUpdating ? 'Updating...' : 'Submit'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

interface DocumentPreviewDialogProps {
  open: boolean;
  docId: number | null;
  onClose: () => void;
}

const DocumentPreviewDialog: React.FC<DocumentPreviewDialogProps> = ({
  open,
  docId,
  onClose
}) => {
  const [documentUrl, setDocumentUrl] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>('');

  useEffect(() => {
    if (open && docId) {
      fetchDocument();
    } else {
      // Reset state when dialog closes
      setDocumentUrl('');
      setError('');
    }
  }, [open, docId]);

  const fetchDocument = async () => {
    if (!docId) return;

    setLoading(true);
    setError('');

    try {
      const response = await getKycDocument(docId);

      if (response?.url) {
        setDocumentUrl(response?.url);
      } else {
        setError('Failed to fetch document URL');
        toast.error('Failed to fetch document');
      }
    } catch (error: any) {
      console.error('Error fetching document:', error);
      setError('Error loading document');
      toast.error('Error loading document');
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = () => {
    if (documentUrl) {
      // Create a temporary anchor element to trigger download
      const link = document.createElement('a');
      link.href = documentUrl;
      link.download = `kyc-document-${docId}`;
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const getFileExtension = (url: string): string => {
    try {
      const urlParts = url.split('?')[0]; // Remove query parameters
      const extension = urlParts.split('.').pop()?.toLowerCase() || '';
      return extension;
    } catch {
      return '';
    }
  };

  const renderFilePreview = () => {
    if (loading) {
      return (
        <Box sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '400px'
        }}>
          <CircularProgress />
          <Typography sx={{ ml: 2 }}>Loading document...</Typography>
        </Box>
      );
    }

    if (error) {
      return (
        <Box sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '400px',
          flexDirection: 'column'
        }}>
          <Typography color="error" variant="h6">
            {error}
          </Typography>
          <Button
            variant="outlined"
            onClick={fetchDocument}
            sx={{ mt: 2 }}
          >
            Retry
          </Button>
        </Box>
      );
    }

    if (!documentUrl) {
      return (
        <Box sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '400px'
        }}>
          <Typography>No document available</Typography>
        </Box>
      );
    }

    const fileExtension = getFileExtension(documentUrl);

    // Handle different file types
    if (fileExtension === 'pdf') {
      return (
        <Box sx={{ width: '100%', height: '600px' }}>
          <iframe
            src={documentUrl}
            width="100%"
            height="100%"
            style={{ border: 'none' }}
            title="Document Preview"
          />
        </Box>
      );
    } else if (['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(fileExtension)) {
      return (
        <Box sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          maxHeight: '600px',
          overflow: 'auto'
        }}>
          <img
            src={documentUrl}
            alt="Document Preview"
            style={{
              maxWidth: '100%',
              maxHeight: '600px',
              objectFit: 'contain'
            }}
          />
        </Box>
      );
    } else {
      // For other file types, show a download option
      return (
        <Box sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '400px',
          flexDirection: 'column'
        }}>
          <Typography variant="h6" gutterBottom>
            Preview not available for this file type
          </Typography>
          <Typography variant="body2" color="textSecondary" gutterBottom>
            File type: {fileExtension.toUpperCase()}
          </Typography>
          <Button
            variant="contained"
            startIcon={<DownloadIcon />}
            onClick={handleDownload}
            sx={{ mt: 2 }}
          >
            Download File
          </Button>
        </Box>
      );
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          minHeight: '80vh',
          maxHeight: '90vh'
        }
      }}
    >
      <DialogTitle sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        pb: 1
      }}>
        <Typography variant="h6">
          KYC Document Preview - Doc ID #{docId}
        </Typography>
        <IconButton
          onClick={onClose}
          sx={{ color: 'grey.500' }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 0 }}>
        {renderFilePreview()}
      </DialogContent>

      {documentUrl && !loading && !error && (
        <DialogActions sx={{ justifyContent: 'space-between', p: 2 }}>
          <Button onClick={onClose} color="inherit">
            Close
          </Button>
          <Button
            variant="contained"
            startIcon={<DownloadIcon />}
            onClick={handleDownload}
            color="primary"
          >
            Download
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
};

const KycModule = () => {
  const [filters, setFilters] = useState({
    userIds: "",
    documentType: "",
    status: "",
    fromDate: null,
    toDate: null,
  });

  const statusDropdown = [
    { label: "Pending", value: "pending" },
    { label: "Approved", value: "approved" },
    { label: "Rejected", value: "rejected" },
  ];

  const documentTypeDropdown = [
    { label: "Aadhaar Front", value: "aadhaar_front" },
    { label: "Aadhaar Back", value: "aadhaar_back" },
    { label: "PAN Card", value: "pan" },
  ];

  const [data, setData] = useState<any[]>([]);
  const [paginationData, setPaginationData] = useState<TablePagination>({
    limit: 10,
    skip: 0,
  });
  const [totalRows, setTotalRows] = useState(0);
  const [isUpdatingStatus, setIsUpdatingStatus] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [userList, setUserList] = useState<{ userId: number; userName: string }[]>([]);
  const [userLoading, setUserLoading] = useState(false);

  const [updateStatusDialog, setUpdateStatusDialog] = useState({
    open: false,
    docId: 0,
    currentStatus: '',
    currentMessage: '',
    isViewOnly: false
  });

  const [documentPreviewDialog, setDocumentPreviewDialog] = useState({
    open: false,
    docId: null as number | null
  });

  useEffect(() => {
    const fetchUserList = async () => {
      setUserLoading(true);
      try {
        const response = await getUserListForFilters();
        if (response?.data) {
          setUserList(response?.data);
        }
      } catch (error) {
        console.error("Error fetching user list", error);
        toast.error("Failed to load user list");
      } finally {
        setUserLoading(false);
      }
    };
    fetchUserList();
    const handler = setTimeout(() => {
      const isUserIdValid = filters.userIds === "" || filters.userIds.length >= 1;
      const areDatesValid =
        (filters.fromDate && filters.toDate) ||
        (!filters.fromDate && !filters.toDate);

      if (isUserIdValid && areDatesValid) {
        loadData();
      }
    }, 800);
    return () => clearTimeout(handler);
  }, [filters, paginationData]);

  const userDropdownOptions = userList.map((user) => ({
    label: user.userName,
    value: user.userId.toString(),
  }));

  const loadData = async () => {
    setLoading(true);
    try {
      const apiParams: any = {};

      if (filters.userIds.trim()) {
        const userIdsArray = filters.userIds.split(',').map(id => parseInt(id.trim())).filter(id => !isNaN(id));
        if (userIdsArray.length === 1) {
          apiParams.userId = userIdsArray[0];
        } else if (userIdsArray.length > 1) {
          apiParams.userId = userIdsArray[0];
        }
      }

      if (filters.documentType) {
        apiParams.documentType = filters.documentType;
      }

      if (filters.status) {
        apiParams.status = filters.status;
      }

      if (filters.fromDate) {
        apiParams.fromDate = formatToDDMMYYYY(filters.fromDate);
      }
      if (filters.toDate) {
        apiParams.toDate = formatToDDMMYYYY(filters.toDate);
      }

      apiParams.limit = paginationData.limit;
      apiParams.skip = paginationData.skip;

      const response = await getKycList(apiParams);

      if (response?.data) {
        setData(response?.data);
        setTotalRows(response?.totalCount || response?.data?.length);
      } else {
        setData([]);
        setTotalRows(0);
      }
    } catch (error) {
      console.error("Error loading KYC data", error);
      toast.error("Error loading KYC data");
      setData([]);
      setTotalRows(0);
    } finally {
      setLoading(false);
    }
  };

  // Helper function to determine if a status can be updated
  const canUpdateStatus = (status: string): boolean => {
    const statusLower = status.toLowerCase();
    return statusLower === 'pending'; // Only pending status can be updated
  };

  const handleOpenUpdateDialog = (docId: number, currentStatus: string, currentMessage: string) => {
    const isViewOnly = !canUpdateStatus(currentStatus);
    setUpdateStatusDialog({
      open: true,
      docId,
      currentStatus,
      currentMessage: currentMessage || '',
      isViewOnly
    });
  };

  const handleCloseUpdateDialog = () => {
    setUpdateStatusDialog({
      open: false,
      docId: 0,
      currentStatus: '',
      currentMessage: '',
      isViewOnly: false
    });
  };

  const handleOpenDocumentPreview = (docId: number) => {
    setDocumentPreviewDialog({
      open: true,
      docId
    });
  };

  const handleCloseDocumentPreview = () => {
    setDocumentPreviewDialog({
      open: false,
      docId: null
    });
  };

  const handleUpdateKycStatus = async (docId: number, status: string, message: string) => {
    setIsUpdatingStatus(docId);

    try {
      const body = {
        docId: docId,
        status: status,
        message: message
      };

      const response = await updateKycDocumentStatus(body);

      if (response?.message && response?.message.includes('successfully')) {
        toast.success("KYC document status updated successfully");
        loadData();
        handleCloseUpdateDialog();
      } else {
        const errorMessage = response?.message || "Failed to update KYC document status";
        toast.error(errorMessage);
        console.error('Update failed:', response);
      }
    } catch (error: any) {
      if (error?.response?.data) {
        const errorData = error?.response?.data;
        const errorMessage = errorData?.message || errorData || "Error updating KYC document status";
        toast.error(`API Error: ${errorMessage}`);
      } else if (error?.message) {
        if (error?.message?.includes('Network Error')) {
          toast.error("Network Error: Please check your internet connection");
        } else if (error?.message.includes('timeout')) {
          toast.error("Request Timeout: Please try again");
        } else if (error?.message.includes('404')) {
          toast.error("API Endpoint Not Found: Please check the API configuration");
        } else if (error?.message.includes('500')) {
          toast.error("Server Error: Please try again later");
        } else {
          toast.error(`Error: ${error?.message}`);
        }
      } else {
        toast.error("Unknown error updating KYC document status");
      }
    } finally {
      setIsUpdatingStatus(null);
    }
  };

  const columns: ColumnConfig[] = [
    {
      field: "fullName",
      headerName: "Name",
      width: 180,
      editable: false,
      sortable: false,
      headerClassName: "custom-header",
      align: 'center',
      headerAlign: 'center'
    },
    {
      field: "documentType",
      headerName: "Document Type",
      width: 150,
      editable: false,
      sortable: false,
      headerClassName: "custom-header",
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => {
        const docType = params.value?.replace(/_/g, ' ').replace(/\b\w/g, (l: string) => l.toUpperCase());
        return docType || '-';
      }
    },
    {
      field: "documentPath",
      headerName: "File",
      width: 140,
      editable: false,
      sortable: false,
      headerClassName: "custom-header",
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => {
        return params.value ? (
          <Button
            size="small"
            variant="contained"
            color="primary"
            onClick={() => handleOpenDocumentPreview(params.row.id)}
            sx={{ fontSize: '11px', mb: 3, }}
          >
            View
          </Button>
        ) : (
          <span style={{ color: '#999', fontStyle: 'italic' }}>No document</span>
        );
      }
    },
    {
      field: "message",
      headerName: "Message",
      width: 180,
      editable: false,
      sortable: false,
      headerClassName: "custom-header",
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => {
        return (
          <div style={{
            maxWidth: '170px',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
          }} title={params.value || ''}>
            {params.value || '-'}
          </div>
        );
      }
    },
    {
      field: "status",
      headerName: "Status",
      width: 140,
      editable: false,
      sortable: false,
      headerClassName: "custom-header",
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => {
        const status = params.value?.toLowerCase();
        return (
          <span style={{
            padding: '4px 8px',
            borderRadius: '4px',
            backgroundColor:
              status === 'pending' ? '#fff3cd' :
                status === 'approved' ? '#d4edda' :
                  status === 'rejected' ? '#f8d7da' : 'transparent',
            color:
              status === 'pending' ? '#856404' :
                status === 'approved' ? '#155724' :
                  status === 'rejected' ? '#721c24' : 'inherit',
            fontSize: '12px',
            fontWeight: '500',
          }}>
            {params.value?.charAt(0).toUpperCase() + params.value?.slice(1)}
          </span>
        );
      }
    },
    {
      field: "createdAt",
      headerName: "Upload Date",
      width: 140,
      editable: false,
      sortable: false,
      headerClassName: "custom-header",
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => {
        return params.value ? formatToDDMMYYYY(params.value) : '-';
      }
    },
    {
      field: "action",
      headerName: "Action",
      width: 180,
      sortable: false,
      headerClassName: "custom-header",
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => {
        const canUpdate = canUpdateStatus(params.row.status);
        return (
          <Button
            variant="contained"
            color={canUpdate ? "primary" : "info"}
            size="small"
            onClick={() => handleOpenUpdateDialog(
              params.row.id,
              params.row.status,
              params.row.message
            )}
            disabled={isUpdatingStatus === params.row.id}
            sx={{
              fontSize: '12px',
              minWidth: '110px',
              mb: 3,
            }}
          >
            {isUpdatingStatus === params.row.id
              ? 'Updating...'
              : canUpdate
                ? 'Update Status'
                : 'View Status'}
          </Button>
        );
      },
    }
  ];

  const handleInput = (e: string, type: string) => {
    setFilters((prev) => ({ ...prev, [type]: e }));
    if (type !== 'limit' && type !== 'skip') {
      setPaginationData({ limit: 10, skip: 0 });
    }
  };

  const fileExporter = async () => {
    try {
      const apiParams: any = {};

      if (filters.userIds.trim()) {
        const userIdsArray = filters.userIds.split(',').map(id => parseInt(id.trim())).filter(id => !isNaN(id));
        if (userIdsArray.length === 1) {
          apiParams.userId = userIdsArray[0];
        } else if (userIdsArray.length > 1) {
          apiParams.userId = userIdsArray[0];
        }
      }

      if (filters.documentType) {
        apiParams.documentType = filters.documentType;
      }

      if (filters.status) {
        apiParams.status = filters.status;
      }

      if (filters.fromDate) {
        apiParams.fromDate = formatToDDMMYYYY(filters.fromDate);
      }
      if (filters.toDate) {
        apiParams.toDate = formatToDDMMYYYY(filters.toDate);
      }

      apiParams.limit = totalRows || 10000;
      apiParams.skip = 0;

      const response = await getKycList(apiParams);

      if (response?.data && response?.data.length > 0) {
        toast.success("Exported successfully.");
        return response.data;
      } else {
        toast.error("No data available to export.");
        return [];
      }
    } catch (error) {
      toast.error("Failed to fetch export data.");
      return [];
    }
  };

  return (
    <div>
      <div
        style={{
          padding: "20px",
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-start",
        }}
      >
        <span className="redemption-title">Manage KYC</span>
        <div
          className="redemption-header"
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "20px",
            width: "100%",
          }}
        >
          <div style={{ display: "flex", gap: "50px", flexWrap: "wrap", marginTop: 10, marginBottom: 40 }}>
            <DropdownSearch
              searchTxt={filters.userIds}
              handleChange={(e: any) => handleInput(e, "userIds")}
              placeholder="Select User"
              label="User Search"
              data={userDropdownOptions}
              searchable={true}
              cleanable={true}
            />
            <DropdownSearch
              searchTxt={filters.documentType}
              handleChange={(e: any) => handleInput(e, "documentType")}
              placeholder="Select Document Type"
              label="Document Type"
              data={documentTypeDropdown}
              searchable={true}
              cleanable={true}
            />
            <DropdownSearch
              searchTxt={filters.status}
              handleChange={(e: any) => handleInput(e, "status")}
              placeholder="Select a Status"
              label="Status"
              data={statusDropdown}
              searchable={true}
              cleanable={true}
            />
            <DateInput
              handleInput={(e) => handleInput(e, "fromDate")}
              searchTxt={filters.fromDate}
              placeholder="Select From Date"
              label="From Date"
              minDate={filters.toDate}
            />
            <DateInput
              handleInput={(e) => handleInput(e, "toDate")}
              searchTxt={filters.toDate}
              placeholder="Select To Date"
              label="To Date"
              maxDate={filters.fromDate}
            />
          </div>
        </div>

        <div className="table-container">
          <div style={{ display: "flex", justifyContent: "flex-start", padding: "10px 10px 0px 10px", backgroundColor: "#fff" }}>
            <ExporterButton exporter={fileExporter} reportName="KYC Report" />
          </div>
          <CustomTable
            editMode={"row"}
            data={data}
            columns={columns}
            getRowId={(ele: any) => ele?.id}
            loading={loading}
          />
        </div>

        <UpdateKycStatusDialog
          open={updateStatusDialog.open}
          currentStatus={updateStatusDialog.currentStatus}
          currentMessage={updateStatusDialog.currentMessage}
          docId={updateStatusDialog.docId}
          onClose={handleCloseUpdateDialog}
          onUpdate={handleUpdateKycStatus}
          isUpdating={isUpdatingStatus !== null}
          isViewOnly={updateStatusDialog.isViewOnly}
        />

        <DocumentPreviewDialog
          open={documentPreviewDialog.open}
          docId={documentPreviewDialog.docId}
          onClose={handleCloseDocumentPreview}
        />
      </div>
    </div>
  );
};

export default KycModule;